import { FC, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { IconSearch } from '@tabler/icons-react';
import { Button, IconButton, Input, Select } from '@atoms';
import { usePartnerCustomers } from '@hooks';
import { EMPTY_FILTERS, FILTER_FIELDS, UsersSearchFormValues } from './constants';
import styles from './styles';

type CustomerOption = {
  id: number;
  legalName: string;
};

interface SearchBoxUsersProps {
  filters: UsersSearchFormValues;
  onSearch: (filters: UsersSearchFormValues) => void;
}

/**
 * Search box to filter users by name, last name, email or company (client-side)
 */
const SearchBoxUsers: FC<SearchBoxUsersProps> = ({ filters, onSearch }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const responsive = useMediaQuery(theme.breakpoints.down('md'));
  const {
    data: customersData,
    isLoading: isLoadingCustomers,
    error: customersError,
  } = usePartnerCustomers();

  const formik = useFormik<UsersSearchFormValues>({
    initialValues: filters,
    enableReinitialize: true,
    onSubmit: (values) => {
      onSearch({
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        email: values.email.trim(),
        customerId: values.customerId,
      });
    },
  });

  const { values, errors, handleBlur, handleChange, handleSubmit, resetForm } = formik;

  /**
   * Clears the search form and resets the applied filters
   */
  const handleClearFilters = () => {
    resetForm({ values: EMPTY_FILTERS });
    onSearch(EMPTY_FILTERS);
  };

  /**
   * Handles the form submit event
   * @param event - The form submit event
   */
  const handleFormSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    handleSubmit();
  };

  return (
    <Box sx={styles.searchContainer} component='form' onSubmit={handleFormSubmit}>
      <Box sx={styles.header}>
        <Typography variant='h4'>{t('USERS_MANAGEMENT:SEARCH_USERS')}</Typography>
      </Box>
      <Box sx={styles.content}>
        <Box sx={styles.firstLine(responsive)}>
          <Box sx={styles.inputGrid(responsive)}>
            {FILTER_FIELDS.map(({ name, label }) => (
              <Box key={name} sx={styles.field(responsive)}>
                <Input
                  label={label}
                  variant='outlined'
                  size='small'
                  name={name}
                  value={values[name]}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  helperText={errors[name]}
                  error={Boolean(errors[name])}
                  fullWidth
                  margin='none'
                />
              </Box>
            ))}
            <Box sx={styles.field(responsive)}>
              <Select
                label={t('COMMON:COMPANY')}
                name='customerId'
                value={values.customerId}
                onChange={handleChange}
                onBlur={handleBlur}
                error={Boolean(errors.customerId)}
                helperText={errors.customerId}
                items={customersData || []}
                isLoading={isLoadingCustomers}
                isError={Boolean(customersError)}
                placeholder='COMMON:SELECT_COMPANY'
                parseOption={(item: CustomerOption) => ({
                  label: item.legalName,
                  value: String(item.id),
                })}
                customStyle={{ width: '100%' }}
              />
            </Box>
          </Box>
          <Box sx={styles.actionsContainer(responsive)}>
            <Button
              customStyle={styles.clearButton}
              text={t('COMMON:CLEAR_FILTER')}
              color='primary'
              variant='link'
              type='button'
              onClick={handleClearFilters}
            />
            <IconButton
              tooltip='COMMON:SEARCH'
              onClick={() => handleSubmit()}
              IconComponent={IconSearch}
              variant='contained'
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default SearchBoxUsers;
